import AxiosUtils from 'utils/axios-utils';
import {
  ImportLogMeta,
  ImportPermission,
  ImportResult,
  ImportSummary,
  LogKind,
  MatchAction,
  MonthStatus,
  MonthStatusCode,
} from './types';

/** Mã backend trả (409) khi MOD chưa được / vừa bị thu quyền nhập — đặc tả mục 8. */
export const NOT_ALLOWED_CODE = 'DATA_IMPORT_NOT_ALLOWED';

/**
 * Mã 409 của cổng chặn Studio (docs/studio-quan-ly/SPEC-TRIEN-KHAI-STUDIO.md, mục 3.3): Quản lý
 * Studio không được nhập dữ liệu dù Admin đã bật quyền. Thân lỗi dạng StudioError:
 * `{ statusCode: 409, message: 'STUDIO_ROUTE_BLOCKED', error: '<câu tiếng Việt>' }`.
 */
export const STUDIO_BLOCKED_CODE = 'STUDIO_ROUTE_BLOCKED';

/** Câu dự phòng khi máy chủ chỉ gửi mã, không gửi câu (khớp STUDIO_ERROR_TEXT của máy chủ). */
export const STUDIO_BLOCKED_TEXT =
  'Quản lý Studio không dùng được chức năng này. Việc này do Admin làm.';

/** File gốc của tháng đã chốt: API cũ, giữ nguyên (đặc tả mục 8.2). */
export const MONTH_ARCHIVE_URL = '/users/tiktok-import/archive';

/** Backend nhận tối đa 10 MB (file TikTok ~2.500 dòng khoảng 530 KB). */
export const MAX_FILE_MB = 10;

/**
 * Chữ trong bảng khi lần tải vừa rồi bị lỗi. Thông báo lỗi tự tắt sau vài giây, nên bảng không
 * được ghi "chưa có…" như lúc thật sự trống.
 */
export const LOAD_FAILED_TEXT = 'Chưa tải được dữ liệu. Bấm Tải lại để thử lại.';

export const LOG_KIND_META: Record<LogKind, { label: string; color: string }> = {
  DAILY: { label: 'File ngày', color: 'green' },
  MONTHLY: { label: 'File tháng', color: 'purple' },
  MANUAL: { label: 'Nhập tay', color: 'orange' },
  GRANT: { label: 'Cấp quyền', color: 'cyan' },
  REVOKE: { label: 'Thu quyền', color: 'red' },
};

export const ACTION_META: Record<MatchAction, { label: string; color: string; help: string }> = {
  OK: { label: 'Đã khớp', color: 'green', help: 'Đã gắn UID, TikTok ID trùng khớp' },
  LINK: {
    label: 'Gắn UID',
    color: 'purple',
    help: 'Tài khoản có username trùng TikTok ID trong file, chưa có UID → gắn UID',
  },
  RENAME: {
    label: 'TikTok ID khác',
    color: 'cyan',
    help: 'TikTok ID trong file khác tên tài khoản; số liệu vẫn liên kết theo UID',
  },
  RENAME_CONFLICT: {
    label: 'Trùng tên',
    color: 'red',
    help: 'TikTok ID trong file đang thuộc tài khoản khác — cần kiểm tra tay',
  },
  RENAME_SKIPPED: {
    label: 'Tên khác',
    color: 'gold',
    help: 'TikTok ID khác tên tài khoản; không đổi tên tài khoản từ file',
  },
  LINK_CONFLICT: {
    label: 'Xung đột UID',
    color: 'volcano',
    help: 'Username trùng nhưng tài khoản đó đã gắn UID khác',
  },
  UNMATCHED: {
    label: 'Chưa có tài khoản',
    color: 'default',
    help: 'Số liệu vẫn được lưu và lên bảng xếp hạng bằng tên TikTok',
  },
};

// ------------------------------------------------------------------ ngày, số

const WEEKDAYS = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];

/**
 * 'YYYY-MM-DD' (ngày TikTok, UTC+0) → 'DD/MM/YYYY'. Cắt chuỗi chứ không qua Date,
 * để máy ở giờ Việt Nam không bị lệch 1 ngày.
 */
export function formatDay(value?: string | null): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value || '');
  return match ? `${match[3]}/${match[2]}/${match[1]}` : '—';
}

/** 'YYYY-MM-DD' → 'DD/MM' (dùng trong câu đã có tháng/năm ở ngay cạnh). */
export function formatDayMonth(value?: string | null): string {
  const match = /^\d{4}-(\d{2})-(\d{2})/.exec(value || '');
  return match ? `${match[2]}/${match[1]}` : '—';
}

/** 'YYYY-MM' → 'MM/YYYY'. */
export function formatMonth(value?: string | null): string {
  const match = /^(\d{4})-(\d{2})/.exec(value || '');
  return match ? `${match[2]}/${match[1]}` : '—';
}

export function formatPeriod(start?: string | null, end?: string | null): string {
  if (!start && !end) return '—';
  if (!start || !end || start === end) return formatDay(start || end);
  return `${formatDay(start)} → ${formatDay(end)}`;
}

/** Thứ trong tuần của một ngày TikTok (tính theo UTC). */
export function weekdayOf(date: string): string {
  const day = new Date(`${date.slice(0, 10)}T00:00:00Z`).getUTCDay();
  return Number.isNaN(day) ? '' : WEEKDAYS[day];
}

const hoursFormat = new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 1 });

/** Tổng thời lượng LIVE: giây → "1.234,5 giờ". */
export function formatLiveHours(seconds?: number | string | null): string {
  return `${hoursFormat.format((Number(seconds) || 0) / 3600)} giờ`;
}

/** Thời lượng LIVE của một Creator: giây → "12g 05p". */
export function formatLiveClock(seconds?: number | null): string {
  const total = Number(seconds) || 0;
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  return `${hours}g ${String(minutes).padStart(2, '0')}p`;
}

export function formatFileSize(bytes?: number | null): string {
  const size = Number(bytes) || 0;
  if (!size) return '';
  if (size < 1024 * 1024) return `${hoursFormat.format(Math.max(1, Math.round(size / 1024)))} KB`;
  return `${hoursFormat.format(size / (1024 * 1024))} MB`;
}

/** Hôm nay / tháng này theo giờ TikTok (UTC+0), tính bằng đồng hồ máy. */
export const utcToday = () => new Date().toISOString().slice(0, 10);
export const utcMonth = () => utcToday().slice(0, 7);

export function shiftMonth(month: string, delta: number): string {
  const [year, number] = month.split('-').map(Number);
  return new Date(Date.UTC(year, number - 1 + delta, 1)).toISOString().slice(0, 7);
}

export const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

/** Link mở đúng tab (và tháng) của trang Nhập dữ liệu. */
export function dataImportLink(tab: 'ngay' | 'thang', month?: string | null): string {
  const params = new URLSearchParams({ tab });
  if (month && MONTH_RE.test(month)) params.set('month', month);
  return `/data-import?${params.toString()}`;
}

// ------------------------------------------------------------ trạng thái tháng

/** Nhãn + màu Tag của trạng thái tháng (DAC-TA-TRANG-THAI-THANG mục 2). */
export const MONTH_STATUS_META: Record<MonthStatusCode, { label: string; color: string }> = {
  RUNNING: { label: 'Đang chạy', color: 'blue' },
  DAILY_COMPLETE: { label: 'Đủ ngày', color: 'green' },
  MONTH_FILE: { label: 'Đã chốt bằng file tháng', color: 'purple' },
  DAILY_INCOMPLETE: { label: 'Thiếu ngày', color: 'red' },
  NO_DATA: { label: 'Chưa có dữ liệu', color: 'orange' },
  LEGACY: { label: 'Số cộng dồn cũ', color: 'gold' },
  FUTURE: { label: 'Chưa tới', color: 'default' },
};

/**
 * Danh sách ngày gộp ngày liền nhau: ['2026-06-05','2026-06-28','2026-06-29','2026-06-30']
 * → "05/06, 28/06 → 30/06". Quá `max` khoảng thì thêm "và N ngày khác".
 */
export function daysListText(days: string[], max = 8): string {
  const sorted = Array.from(new Set(days)).sort();
  const runs: { start: string; end: string; n: number }[] = [];
  for (const day of sorted) {
    const tail = runs[runs.length - 1];
    if (tail && shiftDay(tail.end, 1) === day) {
      tail.end = day;
      tail.n += 1;
    } else runs.push({ start: day, end: day, n: 1 });
  }
  const text = runs
    .slice(0, max)
    .map((run) =>
      run.start === run.end
        ? formatDayMonth(run.start)
        : `${formatDayMonth(run.start)} → ${formatDayMonth(run.end)}`
    )
    .join(', ');
  const rest = runs.slice(max).reduce((sum, run) => sum + run.n, 0);
  return rest > 0 ? `${text} và ${rest} ngày khác` : text;
}

function shiftDay(date: string, delta: number): string {
  return new Date(Date.parse(`${date}T00:00:00Z`) + delta * 86400000).toISOString().slice(0, 10);
}

/** "Việc cần làm" của một tháng trong bảng Tình trạng các tháng. */
export function monthTodo(status: MonthStatus): string {
  const missing = status.missingDays || [];
  switch (status.status) {
    case 'RUNNING':
      return missing.length
        ? `Bù ${missing.length} ngày: ${daysListText(missing, 4)}`
        : 'Nhập file ngày mỗi ngày';
    case 'DAILY_INCOMPLETE':
      return `Bù ${missing.length} ngày ở tab Data ngày: ${daysListText(missing, 4)}`;
    case 'DAILY_COMPLETE':
      return 'Không cần file tháng';
    case 'MONTH_FILE':
      return 'Xong';
    case 'NO_DATA':
      return 'Nhập 1 file cả tháng';
    case 'LEGACY':
      return 'Nhập file cả tháng để thay số cộng dồn';
    default:
      return '—';
  }
}

// ------------------------------------------------------------------ lỗi

// Interceptor axios ném `error.response.data` nên `err` = { statusCode?, code?, message };
// `message` có thể là chuỗi hoặc mảng (class-validator).
function rawMessage(err: any): string | undefined {
  if (Array.isArray(err?.message)) return err.message.join(', ');
  if (typeof err?.message === 'string' && err.message) return err.message;
  return undefined;
}

export function isNotAllowed(err: any): boolean {
  return err?.code === NOT_ALLOWED_CODE || !!rawMessage(err)?.includes(NOT_ALLOWED_CODE);
}

/** 409 STUDIO_ROUTE_BLOCKED: cổng Studio chặn việc nhập (mã nằm ở `code` hoặc `message`). */
export function isStudioBlocked(err: any): boolean {
  return (
    err?.code === STUDIO_BLOCKED_CODE ||
    err?.error === STUDIO_BLOCKED_CODE ||
    !!rawMessage(err)?.includes(STUDIO_BLOCKED_CODE)
  );
}

/**
 * Câu máy chủ gửi kèm STUDIO_ROUTE_BLOCKED để hiện đúng nguyên văn: ưu tiên `error` (StudioError
 * để mã ở `message`, câu ở `error`), rồi `message`. Bỏ qua ô chỉ chứa mã (chữ HOA_GẠCH_DƯỚI) và
 * chữ tiếng Anh mặc định của NestJS ("Conflict"); không còn câu nào thì dùng câu dự phòng.
 */
export function studioBlockedMessage(err: any): string {
  const text = [err?.error, rawMessage(err)].find(
    (value) =>
      typeof value === 'string' &&
      !!value.trim() &&
      !/^[A-Z0-9_]+$/.test(value.trim()) &&
      !/^(Conflict|Forbidden|Bad Request)$/i.test(value.trim())
  );
  return text ? text.trim() : STUDIO_BLOCKED_TEXT;
}

/** Lời báo lỗi: lấy `message` của API; chỉ đổi các câu tiếng Anh của khung NestJS. */
export function importErrorMessage(err: any): string {
  const status: number | undefined = err?.statusCode || err?.status;
  const raw = rawMessage(err);
  if (status === 429 || /ThrottlerException|Too Many Requests/i.test(raw || '')) {
    return 'Bạn thao tác quá nhanh. Hãy đợi khoảng 1 phút rồi thử lại.';
  }
  if (status === 413 || /File too large/i.test(raw || '')) {
    return `File quá lớn (tối đa ${MAX_FILE_MB} MB).`;
  }
  if (status === 404 && /^Cannot (GET|POST|PUT) /i.test(raw || '')) {
    return 'Máy chủ chưa có chức năng Nhập dữ liệu. Hãy báo bộ phận kỹ thuật cập nhật máy chủ rồi tải lại trang.';
  }
  if (raw === 'Forbidden' || raw === 'Unauthorized') {
    return 'Bạn không có quyền thực hiện thao tác này.';
  }
  if (raw === 'Internal server error') return 'Lỗi máy chủ. Vui lòng thử lại sau.';
  return raw || 'Có lỗi xảy ra. Vui lòng thử lại.';
}

/** Request tải file (responseType blob) mà lỗi thì thân lỗi cũng là Blob → đọc lại thành JSON. */
async function unwrapBlobError(err: any): Promise<any> {
  if (typeof Blob === 'undefined' || !(err instanceof Blob)) return err;
  try {
    return JSON.parse(await err.text());
  } catch {
    return {};
  }
}

// ------------------------------------------------------------------ API

/** Tải một file về máy (cùng cách với pages/revenue-manager/creator-ranking.tsx). */
export async function downloadFile(url: string, fileName: string): Promise<void> {
  try {
    const res: any = await AxiosUtils.get(url, undefined, undefined, { responseType: 'blob' });
    const href = URL.createObjectURL(res.data);
    const link = document.createElement('a');
    link.href = href;
    link.download = fileName;
    link.click();
    window.setTimeout(() => URL.revokeObjectURL(href), 1000);
  } catch (err) {
    throw await unwrapBlobError(err);
  }
}

const EMPTY_SUMMARY: ImportSummary = {
  creators: 0,
  rows: 0,
  errors: 0,
  skipped: 0,
  departed: 0,
  OK: 0,
  LINK: 0,
  RENAME: 0,
  RENAME_CONFLICT: 0,
  RENAME_SKIPPED: 0,
  LINK_CONFLICT: 0,
  UNMATCHED: 0,
};

/** Điền mặc định cho các trường mảng/đối tượng để giao diện không vỡ khi thiếu trường. */
export function normalizeResult(data: any): ImportResult {
  const items: ImportResult['items'] = Array.isArray(data?.items) ? data.items : [];
  const sum = (key: 'diamonds' | 'liveSeconds') =>
    items.reduce((total, item) => total + (Number(item[key]) || 0), 0);
  return {
    ...data,
    fileName: data?.fileName || '',
    applied: !!data?.applied,
    usedFallbackColumns: !!data?.usedFallbackColumns,
    periods: Array.isArray(data?.periods) ? data.periods : [],
    summary: { ...EMPTY_SUMMARY, ...(data?.summary || {}) },
    items,
    errors: Array.isArray(data?.errors) ? data.errors : [],
    kind: data?.kind ?? null,
    day: data?.day ?? null,
    month: data?.month ?? null,
    blockers: Array.isArray(data?.blockers) ? data.blockers : [],
    warnings: Array.isArray(data?.warnings) ? data.warnings : [],
    departed: {
      rows: Number(data?.departed?.rows) || 0,
      diamonds: Number(data?.departed?.diamonds) || 0,
    },
    totals: {
      diamonds: Number(data?.totals?.diamonds ?? sum('diamonds')) || 0,
      liveSeconds: Number(data?.totals?.liveSeconds ?? sum('liveSeconds')) || 0,
    },
    existing: data?.existing ?? null,
    monthStatus: data?.monthStatus ?? null,
    reconcile: data?.reconcile ?? null,
    override: data?.override ?? null,
  };
}

export function normalizePermission(row: any): ImportPermission {
  return {
    userId: Number(row?.userId ?? row?.id),
    username: row?.username ?? null,
    nickName: row?.nickName ?? null,
    email: row?.email ?? null,
    avatar: row?.avatar ?? row?.profile?.avatar ?? null,
    allowed: row?.allowed === true || Number(row?.allowed) === 1,
    grantedByName: row?.grantedByName ?? null,
    updatedAt: row?.updatedAt ?? row?.updated_at ?? null,
  };
}

/** `meta` của lịch sử là simple-json: backend có thể trả đối tượng hoặc chuỗi JSON. */
export function parseLogMeta(meta: ImportLogMeta | string | null | undefined): ImportLogMeta {
  if (!meta) return {};
  if (typeof meta !== 'string') return meta;
  try {
    const parsed = JSON.parse(meta);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}
